/**
 * Where someone lands — one decision for web and mobile.
 *
 * Each app used to decide this in its own App.tsx callbacks (mobile's
 * landingTab and tab guard, web's default view), so a new rule meant editing
 * several places in two apps. Here a landing is:
 *
 *   intent (what just happened) + facts (what is true now) → destination
 *
 * decided by an ordered list of small rules, first match wins, then one check
 * that the person may actually see the destination. A new behaviour is a new
 * rule; the apps only carry the destination out (a tab on mobile, a view on
 * web). Pure: no React, no platform, so both apps' tests share it.
 */
/** May this person see this destination? The one access check every landing passes. */
export function canSee(destination, facts) {
    switch (destination) {
        case 'executive':
            return facts.canSeeExecutive;
        case 'locationDashboard':
            return facts.canViewLocationDashboard;
        case 'workOrders':
            return facts.canViewWorkOrders;
        case 'customers':
            return facts.hasLocation && facts.customers.on && facts.customers.canRead;
        case 'reports':
            return true;
    }
}
/**
 * A shop that works by customer opens on its customers: the account has the
 * Customers module on, the person can see customers, and a store is open (the
 * Customers screen is a store's list; with no store it can only say "pick a
 * location"). Waits for the module's answer rather than guessing, so it never
 * lands on the dashboard and then jumps.
 *
 * Not at sign-in for someone who can see the accounts dashboard (Alex,
 * 2026-10-25): managers land on their account page. The phone app reopens
 * the last store at start-up, so without this a manager landed on Customers
 * there but on the account page on the web. Opening a store still shows its
 * customers.
 */
const customersFirst = {
    id: 'customers-first',
    decide: (intent, facts) => {
        if (!facts.hasLocation || !facts.customers.canRead)
            return null;
        if (intent.kind === 'signIn' && (facts.managesAccount ?? facts.canSeeExecutive))
            return null;
        if (!facts.customers.loaded)
            return 'wait';
        return facts.customers.on ? 'customers' : null;
    },
};
/** Opening a location shows that location. */
const openLocationShowsIt = {
    id: 'open-location',
    decide: (intent) => (intent.kind === 'openLocation' ? 'locationDashboard' : null),
};
/** After signing in: the broadest dashboard the person may see. */
const signInDefault = {
    id: 'sign-in-default',
    decide: (intent, facts) => {
        if (intent.kind !== 'signIn')
            return null;
        if (facts.canSeeExecutive)
            return 'executive';
        if (facts.canViewLocationDashboard)
            return 'locationDashboard';
        if (facts.canViewWorkOrders)
            return 'workOrders';
        return 'reports';
    },
};
/** In order; the first rule with an answer decides. */
export const LANDING_RULES = [customersFirst, openLocationShowsIt, signInDefault];
/** Where someone may always go when nothing else fits. */
const FALLBACK_ORDER = ['executive', 'locationDashboard', 'workOrders', 'reports'];
export function resolveLanding(intent, facts, rules = LANDING_RULES) {
    for (const rule of rules) {
        const answer = rule.decide(intent, facts);
        if (answer === 'wait')
            return { status: 'wait' };
        if (answer && canSee(answer, facts))
            return { status: 'go', destination: answer };
    }
    return { status: 'go', destination: FALLBACK_ORDER.find((d) => canSee(d, facts)) ?? 'reports' };
}
/** The destination now, or `fallback` while a fact is still loading (for callers that can't wait, like the logo). */
export function landingNow(intent, facts, fallback) {
    const d = resolveLanding(intent, facts);
    return d.status === 'go' ? d.destination : fallback;
}
